import {
  Button,
  ConfirmDialog,
  EmptyState,
  GridNavProvider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TOUCH,
} from "@nymbl/nos2-rules";
import { useQueryClient } from "@tanstack/react-query";
import { Star } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import type { Pending } from "@/components/time/GridTaskInfo";
import { ariaSort, SortHeader } from "@/components/time/SortHeader";
import { ToggleAllProjects } from "@/components/time/ToggleAllProjects";
import { TypeChip } from "@/components/time/TypeChip";
import { WeekGridDay } from "@/components/time/WeekGridDay";
import {
  type GridRowHandlers,
  TODAY_TINT,
  WeekGridGroupRow,
  WeekGridRow,
} from "@/components/time/WeekGridRow";
import { useSetFavorite } from "@/hooks/use-time-assignable";
import {
  invalidateAfterEntryWrite,
  RULE_REFUSAL,
  useCreateEntry,
  useDeleteEntry,
  useUpdateEntry,
} from "@/hooks/use-time-entries";
import { ApiError } from "@/lib/time/api-client";
import {
  readCollapsedProjects,
  setProjectsOpen,
  writeCollapsedProjects,
} from "@/lib/time/collapsed-projects";
import { dayOfWeek, formatDayHeader, type IsoDate, weekdayShort } from "@/lib/time/dates";
import {
  buildGrid,
  buildSuggestions,
  cellAction,
  clientSaveContext,
  deleteCellTitle,
  type GridCell as GridCellModel,
  type GridRow,
  groupGridRows,
  type Suggestion,
} from "@/lib/time/grid";
import { cellLabel, deleteCellDescription } from "@/lib/time/labels";
import { newId } from "@/lib/time/new-id";
import { formatHours } from "@/lib/time/rules";
import {
  filterGridRows,
  type GridSortKey,
  nextSort,
  type SortState,
  sortGridRows,
} from "@/lib/time/table-view";
import { cn } from "@/lib/utils";
import type { AssignableTask, WeekView } from "@/schemas/time";

interface WeekGridProps {
  view: WeekView;
  assignable: AssignableTask[];
  today: IsoDate;
  /** The day the phone layout opens on (`?day=`); today when unset. */
  selectedDay?: IsoDate;
  /** The task search, typed on the page's "Your week" line. */
  query: string;
  onOpenEntries: GridRowHandlers["onOpenEntries"];
  onSuggestion: (suggestion: Suggestion) => void;
}

const DAY_KEYS: readonly GridSortKey[] = ["day0", "day1", "day2", "day3", "day4", "day5", "day6"];

/** The answer to a write that may have saved (504); the hook already showed its toast. */
const MAY_HAVE_SAVED = 504;

const NOT_ASSIGNED = "Not assigned to this task";

function without<T>(record: Record<string, T>, key: string): Record<string, T> {
  return Object.fromEntries(Object.entries(record).filter(([k]) => k !== key));
}

/** Focuses the shown copy of a grid cell (the phone and desktop layouts both render it). */
function focusCell(label: string) {
  const inputs = document.querySelectorAll<HTMLInputElement>(
    `input[aria-label="${CSS.escape(label)}"]`,
  );
  for (const input of inputs) {
    if (input.offsetParent !== null) {
      input.focus();
      return;
    }
  }
}

interface DeleteAsk {
  row: GridRow;
  cell: GridCellModel;
  hours: number;
}

function dayHeaderSub(day: IsoDate, today: IsoDate): string | undefined {
  if (day === today) return "Today";
  const dow = dayOfWeek(day);
  return dow === 0 || dow === 6 ? "Weekend" : undefined;
}

/**
 * The weekly grid. Keyed by week so "Not saved" values are dropped when the week changes.
 */
export function WeekGrid(props: WeekGridProps) {
  return <WeekGridBody key={props.view.weekStart} {...props} />;
}

function WeekGridBody({
  view,
  assignable,
  today,
  selectedDay,
  query,
  onOpenEntries,
  onSuggestion,
}: WeekGridProps) {
  const input = useMemo(
    () => ({
      weekStart: view.weekStart,
      entries: view.entries,
      assignable,
      plans: view.plans,
      periods: view.periods,
      thresholdPct: view.thresholdPct,
    }),
    [view, assignable],
  );
  const grid = useMemo(() => buildGrid(input), [input]);
  const suggestions = useMemo(() => buildSuggestions(input, grid.rows), [input, grid.rows]);
  const [sort, setSort] = useState<SortState<GridSortKey> | null>(null);
  const filtered = useMemo(() => filterGridRows(grid.rows, query), [grid.rows, query]);
  const rows = useMemo(() => (sort ? sortGridRows(filtered, sort) : filtered), [filtered, sort]);
  // Unsorted, the rows sit under their project (collapsible); a column sort lists them flat.
  const groups = useMemo(() => (sort ? null : groupGridRows(filtered)), [filtered, sort]);
  const order = new Map(
    (groups ? groups.flatMap((g) => g.rows) : rows).map((row, i) => [row.key, i]),
  );
  const [collapsed, setCollapsed] = useState(readCollapsedProjects);
  const toggleGroup = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      writeCollapsedProjects(next);
      return next;
    });
  const setGroups = (keys: string[], open: boolean) =>
    setCollapsed((prev) => {
      const next = setProjectsOpen(prev, keys, open);
      writeCollapsedProjects(next);
      return next;
    });
  const [pending, setPending] = useState<Record<string, Pending>>({});
  // Bumped when a write may have saved (504): it remounts the cell so its draft is dropped,
  // even when the re-read brings back the same hours.
  const [resets, setResets] = useState<Record<string, number>>({});
  // One id per cell until its create succeeds, so a retry after "may have saved" hits the
  // primary key (the server answers "Already saved") instead of adding a copy.
  const createIds = useRef<Record<string, string>>({});
  // TE-25: a cell cleared to empty or 0 asks before it deletes. Focus goes back to that cell.
  const [askDelete, setAskDelete] = useState<DeleteAsk | null>(null);
  const returnTo = useRef<string | null>(null);

  const queryClient = useQueryClient();
  // Rule refusals show on the row chip, so the hooks do not toast them as well.
  const create = useCreateEntry({ quietRefusals: true });
  const update = useUpdateEntry({ quietRefusals: true });
  const remove = useDeleteEntry({ quietRefusals: true });
  const favorite = useSetFavorite();
  const saving = create.isPending || update.isPending || remove.isPending;

  const toggleSort = (key: GridSortKey) =>
    setSort((current) => (current ? nextSort(current, key) : { key, dir: "asc" }));

  async function commit(
    row: GridRow,
    cell: GridCellModel,
    raw: string,
    comment: string | null,
    confirmed = false,
  ) {
    const ctx = clientSaveContext({
      assignable: row.assignable,
      date: cell.date,
      plans: view.plans,
      periods: view.periods,
      thresholdPct: view.thresholdPct,
      demandPersonId: row.demandPersonId,
      excludeHours: cell.entry?.hours ?? 0,
    });
    const action = cellAction({ cell, raw, comment, ctx, today });
    const keep = (next: Pending) => setPending((p) => ({ ...p, [cell.key]: next }));
    const held = (message: string | null): Pending => ({ raw, kind: "blocked", message });
    try {
      switch (action.kind) {
        case "none":
          setPending((p) => without(p, cell.key));
          return;
        case "invalid":
          keep(held(action.message));
          return;
        case "needs-comment":
          keep({
            raw,
            kind: "needs-comment",
            message: `${weekdayShort(cell.date)} needs a comment to save`,
          });
          return;
        case "blocked":
          keep(held(action.message));
          return;
        case "create": {
          // The rules only allow a create on an assignable row, which always has its
          // assignment; held rather than dropped should that ever not hold.
          const demandPersonId = row.assignable?.demandPersonId;
          if (!demandPersonId) {
            keep(held(NOT_ASSIGNED));
            return;
          }
          const id = createIds.current[cell.key] ?? newId();
          createIds.current[cell.key] = id;
          await create.mutateAsync({
            id,
            projectTaskId: row.projectTaskId,
            demandPersonId,
            date: cell.date,
            hours: action.hours,
            comment: action.comment,
          });
          delete createIds.current[cell.key];
          break;
        }
        case "update":
          await update.mutateAsync({
            id: action.entry.id,
            date: cell.date,
            hours: action.hours,
            comment: action.comment,
            openedChangedOn: action.entry.changedOn,
          });
          break;
        case "delete":
          if (!confirmed) {
            setPending((p) => without(p, cell.key));
            returnTo.current = cellLabel(row, cell.date);
            setAskDelete({ row, cell, hours: action.entry.hours });
            return;
          }
          await remove.mutateAsync({
            id: action.entry.id,
            openedChangedOn: action.entry.changedOn,
          });
          break;
      }
      setPending((p) => without(p, cell.key));
    } catch (err) {
      if (err instanceof ApiError && err.status === MAY_HAVE_SAVED) {
        // It may have saved: never call it "Not saved" and never resend on its own. Drop the
        // draft (the reset remounts the cell even if the hours come back unchanged) and
        // re-read the week and sign-offs so the cell shows what the warehouse holds.
        setPending((p) => without(p, cell.key));
        setResets((r) => ({ ...r, [cell.key]: (r[cell.key] ?? 0) + 1 }));
        await invalidateAfterEntryWrite(queryClient);
        return;
      }
      // A rule refusal names the rule on the row chip; anything else was already a toast.
      const refusal = err instanceof ApiError && err.status === RULE_REFUSAL;
      keep(held(refusal ? err.message : null));
    }
  }

  /** Keep: the cell goes back to its saved hours (a remount drops the cleared draft). */
  function keepCell(ask: DeleteAsk) {
    setResets((r) => ({ ...r, [ask.cell.key]: (r[ask.cell.key] ?? 0) + 1 }));
    setAskDelete(null);
  }

  if (grid.rows.length === 0 && grid.pto === null) {
    return <EmptyState icon={Star} title="No tasks yet" />;
  }

  const handlers: GridRowHandlers = {
    pending,
    resets,
    saving,
    favoritePending: favorite.isPending,
    onToggleFavorite: (row) =>
      favorite.mutate({ projectTaskId: row.projectTaskId, isFavorite: !row.isFavorite }),
    onCommit: commit,
    onOpenEntries,
    collapsed,
    onToggleGroup: toggleGroup,
    onSetGroups: setGroups,
  };
  // A project holding a value that did not save stays open, so its prompt is never hidden.
  const isOpen = (rowsOf: GridRow[], key: string) =>
    !collapsed.has(key) || rowsOf.some((r) => r.cells.some((c) => pending[c.key]));

  return (
    <div className="space-y-3">
      <div className="md:hidden">
        {/* Keyed by the asked-for day, so the Today chip reselects it even on this page. */}
        <WeekGridDay
          key={selectedDay ?? ""}
          grid={grid}
          rows={filtered}
          today={today}
          selectedDay={selectedDay}
          {...handlers}
        />
      </div>
      {/* The desktop table. This box is the one that scrolls when narrow; the Table inside is
          not a Tab stop because its hour boxes and sort buttons already are. */}
      <div
        className="hidden overflow-x-auto rounded-lg border bg-card md:block"
        data-testid="week-grid-scroll"
      >
        <div className="min-w-[880px]">
          <GridNavProvider>
            <Table aria-label="Week grid">
              <TableHeader>
                <TableRow>
                  <TableHead
                    aria-sort={ariaSort(sort?.key === "task", sort?.dir)}
                    className="min-w-64"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <SortHeader
                        label="Task"
                        active={sort?.key === "task"}
                        dir={sort?.dir}
                        onClick={() => toggleSort("task")}
                      />
                      <ToggleAllProjects
                        groups={groups ?? []}
                        collapsed={collapsed}
                        pending={pending}
                        onSetAll={setGroups}
                      />
                    </div>
                  </TableHead>
                  {grid.days.map((day, i) => {
                    const key = DAY_KEYS[i] ?? "day0";
                    return (
                      <TableHead
                        key={day}
                        className={cn("text-center", day === today && TODAY_TINT)}
                        aria-sort={ariaSort(sort?.key === key, sort?.dir)}
                      >
                        <SortHeader
                          label={formatDayHeader(day)}
                          sub={dayHeaderSub(day, today)}
                          subTone={day === today ? "today" : "muted"}
                          active={sort?.key === key}
                          dir={sort?.dir}
                          onClick={() => toggleSort(key)}
                          className={day === today ? "text-info" : undefined}
                        />
                      </TableHead>
                    );
                  })}
                  <TableHead
                    className="text-right"
                    aria-sort={ariaSort(sort?.key === "week", sort?.dir)}
                  >
                    <SortHeader
                      label="Week"
                      active={sort?.key === "week"}
                      dir={sort?.dir}
                      onClick={() => toggleSort("week")}
                    />
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {grid.pto && (
                  <TableRow className="bg-muted/40">
                    <TableCell>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold">{grid.pto.label}</span>
                        <TypeChip type="pto" />
                      </div>
                    </TableCell>
                    {grid.pto.cells.map((c) => (
                      <TableCell
                        key={c.date}
                        className={cn(
                          "text-center text-sm font-semibold text-muted-foreground",
                          c.date === today && TODAY_TINT,
                        )}
                      >
                        {c.hours > 0 ? formatHours(c.hours) : ""}
                      </TableCell>
                    ))}
                    <TableCell className="text-right font-semibold">
                      {formatHours(grid.pto.weekHours)}
                    </TableCell>
                  </TableRow>
                )}
                {groups
                  ? groups.map((group) => {
                      const open = isOpen(group.rows, group.key);
                      return [
                        <WeekGridGroupRow
                          key={`group:${group.key}`}
                          group={group}
                          open={open}
                          days={grid.days}
                          today={today}
                          onToggle={() => toggleGroup(group.key)}
                        />,
                        ...(open
                          ? group.rows.map((row) => (
                              <WeekGridRow
                                key={row.key}
                                row={row}
                                today={today}
                                grouped
                                planOnHeading={group.plan !== null}
                                gridRow={order.get(row.key) ?? 0}
                                {...handlers}
                              />
                            ))
                          : []),
                      ];
                    })
                  : rows.map((row) => (
                      <WeekGridRow
                        key={row.key}
                        row={row}
                        today={today}
                        gridRow={order.get(row.key) ?? 0}
                        {...handlers}
                      />
                    ))}
                <TableRow className="bg-muted/40 font-semibold">
                  <TableCell>Day total</TableCell>
                  {grid.dayTotals.map((total, i) => (
                    <TableCell
                      key={grid.days[i]}
                      className={cn("text-center", grid.days[i] === today && TODAY_TINT)}
                    >
                      {total > 0 ? formatHours(total) : "–"}
                    </TableCell>
                  ))}
                  <TableCell className="text-right">{formatHours(grid.weekTotal)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </GridNavProvider>
        </div>
      </div>
      {askDelete && (
        <ConfirmDialog
          open
          onOpenChange={(open) => {
            if (!open) keepCell(askDelete);
          }}
          title={deleteCellTitle(askDelete.hours, askDelete.cell.date)}
          description={deleteCellDescription(askDelete.row, askDelete.cell.date)}
          hideDescription
          confirmLabel="Delete"
          cancelLabel="Keep"
          variant="destructive"
          focusConfirm
          onCloseAutoFocus={(event) => {
            const label = returnTo.current;
            if (!label) return;
            event.preventDefault();
            returnTo.current = null;
            focusCell(label);
          }}
          onConfirm={async () => {
            await commit(askDelete.row, askDelete.cell, "", null, true);
            setAskDelete(null);
          }}
        />
      )}
      {suggestions.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-muted-foreground">Suggested:</span>
          {suggestions.map((s) => (
            <Button
              key={s.demandPersonId}
              type="button"
              variant="outline"
              size="sm"
              className={cn("rounded-full border-dashed border-primary text-primary", TOUCH)}
              onClick={() => onSuggestion(s)}
            >
              + {s.label}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}
